import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import SP_API from 'components/API';
import config from 'config.json';

// Vision UI Dashboard PRO React example components
import Header from 'examples/Header';
import Footer from 'examples/Footer';
import truckImage from 'assets/images/tracking/truck.png';
import vahanLogo from 'assets/images/tracking/vahanHighresolution.png';
import sarathiImage from 'assets/images/tracking/sarathihighresolution.png';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { LockOpen, Lock } from '@mui/icons-material';
import fastagLogo from 'assets/images/tracking/fastagLogoForButton.png';
import styles from './styles.module.scss'; // Changed import to styles.module.scss
import VehicleDetails from './VehicleDetails';
import moment from 'moment';

// Vision UI Dashboard PRO React context and components
import { useVisionUIController, setOpenConfigurator } from 'context';
import Configurator from 'components/Configurator';
import VuiBox from 'components/VuiBox';
import TickIcon from 'examples/Icons/TickIcon';

const perks = [
  '100% Authentic & Government-Verified Data',
  'Enhanced Security',
  'Verify Insurance Compliance',
  'Improve In Plant TAT',
  'Fast And Easy Verification',
  'Fraud Prevention',
];

function VerifyTrucks() {
  const history = useHistory();
  const [controller, dispatch] = useVisionUIController();
  const { openConfigurator } = controller;
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [vehicleData, setVehicleData] = useState(null);
  const [showVehicleDetails, setShowVehicleDetails] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [userDetails, setUserDetails] = useState({});

  const validateVehicleNumber = (number) => {
    const regex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{7,16}$/;
    return regex.test(number);
  };

  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  const checkAuthentication = () => {
    const token = localStorage.getItem('token');
    return token !== null && token !== '';
  };

  // Check authentication status on component mount
  React.useEffect(() => {
    const isAuthenticated = checkAuthentication();
    setLoggedIn(isAuthenticated);
    loadInput();

    if (isAuthenticated) {
      // Get user details from localStorage
      const storedUserDetails = localStorage.getItem('userDetails');
      if (storedUserDetails) {
        try {
          const userDetails = JSON.parse(storedUserDetails);
          setUserDetails(userDetails);
        } catch (e) {
          console.error('Error parsing user details:', e);
          setUserDetails({ name: 'User' });
        }
      } else {
        setUserDetails({ name: 'User' });
      }
    }
  }, []);

  const handleVerify = async () => {
    // Check authentication first
    if (!checkAuthentication()) {
      // Show login form
      saveInput();
      handleConfiguratorOpen();
      return;
    }

    // Clear previous errors and data
    setError('');
    setShowVehicleDetails(false);
    setVehicleData(null);

    // Validate vehicle number
    if (!vehicleNumber.trim()) {
      setError('Please enter a vehicle number');
      return;
    }

    if (!validateVehicleNumber(vehicleNumber.trim())) {
      setError('Please enter a valid vehicle number');
      return;
    }

    setIsLoading(true);

    try {
      const response = await SP_API({
        method: 'POST',
        url: config.VAHAN_API,
        data: {
          licensePlate: vehicleNumber.trim(),
        },
      });

      if (response && response.data) {
        setVehicleData(response.data);
        setShowVehicleDetails(true);
      } else {
        setError('Vehicle not found or invalid registration number');
      }
    } catch (error) {
      console.error('API Error:', error);
      if (error?.status === 400) {
        setError(
          error?.data || 'Failed to fetch vehicle details. Please try again.',
        );
        return;
      }
      setError('Failed to fetch vehicle details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadInput = () => {
    const match = document.cookie.match(/(^| )tempInput=([^;]+)/);
    if (match) {
      setVehicleNumber(decodeURIComponent(match[2]));
      document.cookie =
        'tempInput=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
  };

  const saveInput = () => {
    const expires = new Date(Date.now() + 30 * 1000).toUTCString(); // 30 sec
    document.cookie = `tempInput=${encodeURIComponent(
      vehicleNumber,
    )}; expires=${expires}; path=/`;
  };

  const handleCloseVehicleDetails = () => {
    setShowVehicleDetails(false);
    setVehicleData(null);
    setVehicleNumber('');
  };

  const handleVerifyDriver = () => {
    history.push('/verify-sarathi');
  };

  const handleFastagClick = () => {
    saveInput();
    history.push('/track-fastag');
  };

  let insuranceValidity = null;
  if (vehicleData && vehicleData.insuranceValidity) {
    insuranceValidity = moment(vehicleData.insuranceValidity).isAfter(moment())
      ? 'Valid'
      : 'Expired';
  }

  // Floating lock button (same as homepage)
  const configsButton = (
    <VuiBox
      id="floating_lock_button"
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="5rem"
      zIndex={99}
      color="white"
      sx={({ palette: { info, success } }) => ({
        cursor: 'pointer',
        backgroundColor: loggedIn ? success.main : info.main,
      })}
      onClick={handleConfiguratorOpen}
    >
      {loggedIn ? (
        <Lock id="floating_lock_button" fontSize="default" color="inherit">
          {userDetails.name ? userDetails.name.split(' ')[0] : 'User'}
        </Lock>
      ) : (
        <LockOpen id="floating_lock_button" fontSize="default" color="inherit">
          Login
        </LockOpen>
      )}
    </VuiBox>
  );

  return (
    <>
      <Header />

      <main className={styles.heroSection}>
        {/* Background truck illustration placeholder */}

        {!showVehicleDetails && (
          <div className={styles.heroContent}>
            <div className={styles.truckIllustration}>
              <img src={truckImage} alt="Truck" className={styles.truckImage} />
            </div>
            <div className={styles.vahanTextContainer}>
              <div className={styles.vahanLogoContainer}>
                <h1 className={styles.mainHeadline}>
                  Verify Trucks with Vahan!
                </h1>
                <img
                  src={vahanLogo}
                  alt="Vahan Logo"
                  className={styles.vahanLogoImage}
                />
              </div>

              <p className={styles.subtitle}>
                Verify Before You Trust - Protect Your Shipment. Stay Compliant.
              </p>
            </div>
          </div>
        )}

        {!showVehicleDetails ? (
          <div className={styles.inputSectionContainer}>
            <form
              onSubmit={(e) => {
                e.preventDefault(); // prevents page reload
                handleVerify();
              }}
              className={styles.inputElementsContainer}
            >
              <input
                type="text"
                placeholder="Enter Vehicle Registration No., eg KA03N2536"
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                className={styles.vehicleInput}
              />
              <button
                type="button"
                onClick={handleVerify}
                disabled={isLoading}
                className={styles.verifyButton}
              >
                {isLoading ? 'Verifying...' : 'Get Details'}
              </button>
            </form>
          </div>
        ) : (
          <div className={styles.inputSectionContainerVerified}>
            <div
              className={styles.backButton}
              onClick={handleCloseVehicleDetails}
            >
              <ChevronLeftIcon fontSize="large" />
              <span>Back</span>
            </div>

            <form className={styles.inputElementsContainerVerified}>
              <input
                type="text"
                placeholder="Enter Vehicle Registration No., eg KA03N2536"
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                className={styles.vehicleInputVerified}
              />
              <button
                onClick={handleVerify}
                disabled={isLoading}
                className={styles.verifyButtonVerified}
              >
                {isLoading ? (
                  'Verifying...'
                ) : (
                  <>
                    Get<span className={styles.dynamicGet}> Details</span>
                  </>
                )}
              </button>

              <div
                className={styles.verifyDriverButtonVerified}
                onClick={handleVerifyDriver}
              >
                <img
                  src={sarathiImage}
                  alt="Driver"
                  className={styles.sarathiImage}
                />
                <span className={styles.sarathiTextVerified}>
                  Verify Driver
                </span>
              </div>
            </form>
          </div>
        )}
        {/* Error Display */}
        {error && <div className={styles.errorMessage}>{error}</div>}

        {showVehicleDetails && (
          <div className={styles.vehicleDetailsCard}>
            <div className={styles.vehicleHeader}>
              <div className={styles.vehicleNumberContainer}>
                <div className={styles.vehicleNumberSection}>
                  <h2 className={styles.vehicleNumber}>
                    {vehicleData?.licensePlate}
                  </h2>
                  <div className={styles.vahanBrand}>
                    <img
                      src={vahanLogo}
                      alt="Vahan Logo"
                      className={styles.vahanLogoImage}
                    />
                  </div>
                </div>
                <div
                  className={styles.fastagButton}
                  onClick={handleFastagClick}
                >
                  <img
                    src={fastagLogo}
                    alt="Fastag Logo"
                    className={styles.fastagLogo}
                  />
                  <span className={styles.buttonText}>
                    Get Vehicle Location
                  </span>
                </div>
              </div>

              <div className={styles.vehicleStatusPanels}>
                <div className={styles.statusPanel}>
                  <span className={styles.statusLabel}>RC Status</span>
                  <span
                    className={`${styles.statusValue} ${
                      vehicleData.RCStatus === 'ACTIVE'
                        ? styles.rcActive
                        : styles.rcInactive
                    }`}
                  >
                    {vehicleData.RCStatus}
                  </span>
                </div>
                <div className={styles.statusPanel}>
                  <span className={styles.statusLabel}>Insurance Status</span>
                  <span
                    className={`${styles.statusValue} ${
                      insuranceValidity === 'Expired'
                        ? styles.insuranceExpired
                        : styles.insuranceActive
                    }`}
                  >
                    {insuranceValidity}
                  </span>
                </div>
                <div className={styles.statusPanel}>
                  <span className={styles.statusLabel}>Vehicle Type</span>
                  <span className={styles.statusValue}>
                    {vehicleData.bodyType}
                  </span>
                </div>
                <div className={styles.statusPanel}>
                  <span className={styles.statusLabel}>Gross Weight</span>
                  <span
                    className={styles.statusValue}
                  >{`${vehicleData.grossWeight} MT`}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Bottom Section */}
      <section className={styles.bottomSection}>
        {!showVehicleDetails && (
          <div className={styles.perks}>
            <div className={styles.bottomHeading}>
              Get real-time, official vehicle details & prevent frauds
            </div>
            <div className={styles.perksList}>
              {perks.map((perk, index) => (
                <div key={index} className={styles.perksListItem}>
                  <TickIcon
                    className={styles.tickIcon}
                    width={20}
                    height={20}
                    fill="#A2B2C4"
                  />
                  <p className={styles.perksListItemText}>{perk}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {showVehicleDetails && (
          <VehicleDetails
            vehicleData={vehicleData}
            onClose={handleCloseVehicleDetails}
          />
        )}
        {/* <img src={clients} alt="Our Clients" className={styles.clientsImage} /> */}
      </section>
      <Footer />
      <Configurator
        userDetails={loggedIn ? userDetails : null}
        isShowConfig={!openConfigurator}
      />
      {configsButton}
    </>
  );
}

export default VerifyTrucks;
